import React, { useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { levels } from '../data/levels';
import styles from './Home.module.css';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const onboardingComplete = useAppStore((s) => s.onboardingComplete);
  const completedLevels = useAppStore((s) => s.completedLevels);
  const currentStage = useAppStore((s) => s.currentStage);
  const streak = useAppStore((s) => s.streak);
  const currentTitle = useAppStore((s) => s.getCurrentTitle());
  const totalStars = useAppStore((s) => s.getTotalStars());
  const stageRate = useAppStore((s) => s.getStageCompletionRate());

  // 追踪首页访问（仅 mount 时触发一次）
  const visited = useRef(false);
  useEffect(() => {
    if (!visited.current) {
      visited.current = true;
      useAppStore.getState().recordHomeVisit();
    }
  }, []);

  // 每日推荐：在当前阶段找第一个未完成的关卡
  const dailyRecommend = useMemo(() => {
    // 优先读 store 里的推荐（可能是完成上一关后自动更新的）
    const dr = useAppStore.getState().dailyRecommend;
    if (dr && dr.date === new Date().toISOString().slice(0, 10) && !dr.completed) {
      const rec = levels.find((l) => l.id === dr.recommendedLevelId);
      if (rec) return rec;
    }
    // 回退：本地计算
    const completedIds = new Set(completedLevels.map((cl) => cl.levelId));
    return levels.find(
      (l) => l.stage === currentStage && !completedIds.has(l.id)
    ) || null;
  }, [currentStage, completedLevels]);

  // 连胜里程碑进度
  const nextMilestone = useMemo(() => {
    const milestones = [3, 7, 14, 30];
    for (const m of milestones) {
      if (streak.currentStreak < m) return m;
    }
    return null;
  }, [streak.currentStreak]);

  const milestoneProgress = nextMilestone
    ? Math.round((streak.currentStreak / nextMilestone) * 100)
    : 100;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>育儿成长闯关</h1>
        <p className={styles.subtitle}>让全家用同一套方法，学会照顾 0-12 月宝宝</p>
      </header>

      {/* 已登录状态：趣味性面板 */}
      {onboardingComplete && (
        <section className={styles.funPanel}>
          {/* 连胜条 */}
          {streak.currentStreak > 0 && (
            <div className={styles.streakBar}>
              <span className={styles.streakIcon}>
                {streak.currentStreak >= 30 ? '🔥' : streak.currentStreak >= 14 ? '🔥' : streak.currentStreak >= 7 ? '🔥' : '🔥'}
              </span>
              <div className={styles.streakInfo}>
                <span className={styles.streakCount}>连续学习 <strong>{streak.currentStreak}</strong> 天</span>
                {nextMilestone && (
                  <div className={styles.streakProgressBar}>
                    <div
                      className={styles.streakProgressFill}
                      style={{ width: `${milestoneProgress}%` }}
                    />
                  </div>
                )}
                {nextMilestone ? (
                  <span className={styles.streakGoal}>距 {nextMilestone} 天里程碑还差 {nextMilestone - streak.currentStreak} 天</span>
                ) : (
                  <span className={styles.streakGoal}>🏆 已解锁全部里程碑！</span>
                )}
              </div>
              <span className={styles.longestStreak}>最长 {streak.longestStreak} 天</span>
            </div>
          )}

          {/* 状态摘要 */}
          <div className={styles.statusSummary}>
            <span className={styles.summaryItem}>{currentTitle}</span>
            <span className={styles.summaryItem}>⭐ {totalStars} 颗</span>
            <span className={styles.summaryItem}>新生儿期 {stageRate}%</span>
          </div>

          {/* 每日推荐 */}
          {dailyRecommend && (
            <div className={styles.dailyRec}>
              <h3 className={styles.dailyRecTitle}>📌 今日推荐</h3>
              <div
                className={styles.dailyRecCard}
                onClick={() => navigate(`/level/${dailyRecommend.id}`)}
              >
                <div className={styles.dailyRecContent}>
                  <span className={styles.dailyRecModule}>[{dailyRecommend.module}]</span>
                  <span className={styles.dailyRecName}>{dailyRecommend.title}</span>
                  <span className={styles.dailyRecMeta}>
                    预计 {dailyRecommend.estimatedTime} 分钟
                  </span>
                </div>
                <button
                  className={styles.dailyRecBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/level/${dailyRecommend.id}`);
                  }}
                >
                  开始学习
                </button>
              </div>
            </div>
          )}

          {/* 继续学习/查看地图 */}
          <div className={styles.quickActions}>
            {completedLevels.length > 0 && (
              <button
                className={styles.quickBtn}
                onClick={() => {
                  const lastId = completedLevels[completedLevels.length - 1].levelId;
                  navigate(`/level/${lastId}`);
                }}
              >
                📖 继续上次学习
              </button>
            )}
            <button className={styles.quickBtnSecondary} onClick={() => navigate('/map')}>
              🗺️ 学习地图
            </button>
          </div>

          {/* 修改学习路径 */}
          <div className={styles.editPath}>
            <button className={styles.editPathBtn} onClick={() => navigate('/onboarding')}>
              ✏️ 修改学习路径
            </button>
            <button
              className={styles.resetBtn}
              onClick={() => {
                if (window.confirm('确定要重置所有学习进度吗？此操作不可恢复。')) {
                  useAppStore.getState().resetProgress();
                  window.location.reload();
                }
              }}
            >
              🔄 重置学习进度
            </button>
          </div>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>故事区</h2>
        <div className={styles.storyBox}>
          <p>第一次抱宝宝、第一次喂奶、第一次半夜哭醒……</p>
          <p>新手家庭需要的不是更多碎片信息，而是一条清晰、可信、能练习的路径。</p>
        </div>
      </section>

      <section className={`${styles.section} ${styles.painSection}`}>
        <h2 className={styles.sectionTitle}>为什么要系统学习？</h2>
        <ul className={styles.painPoints}>
          <li>信息碎片太多，关键时刻想不起来</li>
          <li>家人经验不同，容易产生分歧</li>
          <li>看过不等于会判断、会处理</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>你会得到什么</h2>
        <div className={styles.benefits}>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>📅</span>
            <span>按月龄学习</span>
          </div>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>🎯</span>
            <span>情景练习</span>
          </div>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>📚</span>
            <span>错题复习</span>
          </div>
          <div className={styles.benefitItem}>
            <span className={styles.benefitIcon}>📊</span>
            <span>掌握度反馈</span>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>0-12 月学习路径预览</h2>
        <div className={styles.pathPreview}>
          <div className={styles.stage}>0-28天</div>
          <div className={styles.arrow}>→</div>
          <div className={styles.stage}>1-3月</div>
          <div className={styles.arrow}>→</div>
          <div className={styles.stage}>4-6月</div>
          <div className={styles.arrow}>→</div>
          <div className={styles.stage}>7-12月</div>
        </div>
        <div className={styles.modules}>
          <span>喂养</span>
          <span>睡眠</span>
          <span>护理</span>
          <span>安全</span>
          <span>发育</span>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>内容提示</h2>
        <div className={styles.disclaimer}>
          <p>本产品用于家庭育儿知识学习，不替代医生或专业医疗建议。</p>
        </div>
      </section>

      {!onboardingComplete && (
        <section className={styles.ctaSection}>
          <button className={styles.primaryButton} onClick={() => navigate('/onboarding')}>
            开始建立宝宝学习路径
          </button>
          <button className={styles.secondaryButton} onClick={() => navigate('/map')}>
            我先看看阶段地图
          </button>
        </section>
      )}

      <footer className={styles.footer}>
        <p>育儿成长闯关 © 2026</p>
      </footer>
    </div>
  );
};

export default Home;